// Kill Sheet — Surface Line Displacement tracking (Module 4.1a).
//
// WHAT THIS FILE IS
// -----------------
// Pure logic for ONE tracked surface-line displacement on a well-scoped
// Kill Sheet. It answers three separate questions and keeps them separate:
//
//   1. What is the authoritative Surface Line Volume for this sheet?
//      -> Rig Data Sheet Card No. 4 (`wellControlVolumes.surfaceLineVolume`)
//         of the rig THIS kill sheet is associated with. Never a global
//         "last used" rig, never a second input on the kill sheet.
//   2. How many pump strokes does that volume represent?
//      -> volume (bbl) / effective pump output (bbl/stk). The pump output
//         inherited onto the kill sheet ALREADY has volumetric efficiency
//         applied (lib/rigsheet/compute.js -> computePumpDisplacement), so
//         efficiency is never applied a second time here.
//   3. Where is the operation now?
//      -> from manual absolute pump-counter entries measured against an
//         explicitly confirmed baseline reading.
//
// WHAT IT IS NOT
// --------------
// Not a measurement. Every stroke number here originates from a manual
// pump-counter entry typed by the operator. Reaching a calculated stroke
// target is an estimated displacement milestone, not confirmation of fluid
// position and not "well killed".
//
// Nothing in this file touches pressure formulas, kill-method logic, the
// Driller's Schedule or any existing volume/stroke total. The surface-line
// stage is accounted for on its own; the drill-string stage keeps using the
// existing string-internal-volume strokes (which never included the surface
// lines).

import { getRigSheet as _getRigSheet } from "@/lib/rigsheet/storage";
import { toImperialValue } from "@/lib/metricUnits";

export const SURFACE_DISPLACEMENT_VERSION = 1;

// Comparisons against the unrounded boundary need a tolerance so binary
// float noise can't leave a stage "0.0000000001 strokes short".
const EPS = 1e-9;

const _num = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const _now = () => new Date().toISOString();
const _id = () => `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// ---------------------------------------------------------------------------
// 1. Source resolution — Rig Data Sheet Card No. 4
// ---------------------------------------------------------------------------

/**
 * resolveCard4SurfaceVolume(rigId, deps?) → source descriptor.
 *
 * `state` is deliberately granular so the UI can handle each case
 * separately and so missing information can never collapse into zero:
 *   "missing-rig"      — the kill sheet has no rig association.
 *   "missing-rig-sheet"— the associated rig's data sheet is gone.
 *   "missing-value"    — Card No. 4 has no Surface Line Volume entered.
 *   "invalid"          — Card No. 4 holds a non-numeric or negative value.
 *   "zero"             — complete, valid Card No. 4 data that equals zero.
 *   "ok"               — complete, valid, positive volume.
 */
export function resolveCard4SurfaceVolume(rigId, deps = {}) {
  const getRigSheet = deps.getRigSheet || _getRigSheet;
  const base = {
    state: "missing-rig", rigId: rigId || "", rigName: "",
    rigUnits: "", sourceValue: null, sourceUnit: "", volumeBbl: null,
    sourcePath: "Rig Data Sheet · Card 4 · Well Control Volumes",
  };
  if (!rigId) return base;

  let rs = null;
  try { rs = getRigSheet(rigId); } catch { rs = null; }
  if (!rs || !rs.payload) return { ...base, state: "missing-rig-sheet" };

  const p = rs.payload;
  const rigUnits = p.units === "SI" ? "SI" : "IMP";
  const out = {
    ...base,
    rigName: p.rigName || "",
    rigUnits,
    sourceUnit: rigUnits === "SI" ? "m³" : "bbl",
  };

  const raw = p.wellControlVolumes ? p.wellControlVolumes.surfaceLineVolume : undefined;
  if (raw === "" || raw == null) return { ...out, state: "missing-value", sourceUnit: out.sourceUnit };

  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) {
    return { ...out, state: "invalid", sourceValue: String(raw) };
  }

  // Preserve the source value AND its unit; convert to bbl through the
  // existing conversion utility only when the rig sheet is metric.
  const volumeBbl = rigUnits === "SI" ? Number(toImperialValue(n, "bbl", "metric")) : n;
  return {
    ...out,
    state: n === 0 ? "zero" : "ok",
    sourceValue: n,
    volumeBbl,
  };
}

/**
 * resolveRigPump(rigId, deps?) → { pumpId, label, dispBblPerStk } | null
 *
 * Mirrors the selection rule the `rig.pumpOutputBblStk` deriver uses
 * (designated KILL pump first, then the designated circulating pump, then
 * the first pump with a computed displacement) so the kill sheet and the
 * dashboard never disagree about WHICH pump the inherited output came
 * from. The number itself is read from the rig sheet's own
 * `computed.dispBblPerStk`, which already includes volumetric efficiency.
 */
export function resolveRigPump(rigId, deps = {}) {
  const getRigSheet = deps.getRigSheet || _getRigSheet;
  if (!rigId) return null;
  let rs = null;
  try { rs = getRigSheet(rigId); } catch { rs = null; }
  const p = (rs && rs.payload) || null;
  if (!p || !Array.isArray(p.pumps) || p.pumps.length === 0) return null;
  const killId = (p.defaults && p.defaults.defaultKillPumpId) || "";
  const circId = (p.defaults && p.defaults.defaultCirculatingPumpId) || "";
  const designated = (killId && p.pumps.find((x) => x.id === killId))
    || (circId && p.pumps.find((x) => x.id === circId))
    || null;
  const picked = designated
    || p.pumps.find((x) => _num(x.computed && x.computed.dispBblPerStk) != null);
  if (!picked) return null;
  return {
    pumpId: picked.id || "",
    label: [picked.label || "Pump", picked.pumpType || ""].filter(Boolean).join(" · "),
    dispBblPerStk: _num(picked.computed && picked.computed.dispBblPerStk),
    // True when the pump was explicitly designated on the Rig Data Sheet
    // (kill or circulating), as opposed to falling back to the first pump
    // that happens to carry a computed displacement.
    isRigDefault: Boolean(designated && picked.id === designated.id),
  };
}

// ---------------------------------------------------------------------------
// 2. Stroke target
// ---------------------------------------------------------------------------

/**
 * computeSurfaceStageTarget({ volumeBbl, pumpOutputBblStk }) →
 *   { ok, targetStrokes, wholeStrokeTarget } | { ok:false, code, message }
 *
 * targetStrokes is the UNROUNDED boundary and is what every downstream
 * accounting decision uses. wholeStrokeTarget is a display threshold only.
 */
export function computeSurfaceStageTarget({ volumeBbl, pumpOutputBblStk }) {
  const vol = _num(volumeBbl);
  const disp = _num(pumpOutputBblStk);
  if (vol == null) {
    return { ok: false, code: "volume-missing",
      message: "Surface Line Volume is not available from Rig Data Sheet Card 4." };
  }
  if (vol < 0) {
    return { ok: false, code: "volume-negative",
      message: "Surface Line Volume on Card 4 is negative. Correct Card 4 before tracking." };
  }
  if (disp == null || disp <= 0) {
    return { ok: false, code: "pump-invalid",
      message: "Effective pump output per stroke must be a positive number. Set Pump output (bbl/stk) in Frozen Inputs." };
  }
  const targetStrokes = vol / disp;
  return {
    ok: true,
    targetStrokes,
    wholeStrokeTarget: Math.max(0, Math.ceil(targetStrokes - EPS)),
    pumpOutputBblStk: disp,
    volumeBbl: vol,
  };
}

/**
 * Identity of the source data an operation was started against. Any change
 * to this string means Card 4 or the pump output moved underneath an
 * operation — surfaced as a warning, never silently applied.
 */
export function sourceSignature({ rigId, sourceValue, sourceUnit, pumpOutputBblStk }) {
  return [rigId || "", sourceValue == null ? "" : String(sourceValue),
    sourceUnit || "", pumpOutputBblStk == null ? "" : String(pumpOutputBblStk)].join("|");
}

// ---------------------------------------------------------------------------
// 3. Operation record (persisted on the kill sheet as `rec.displacement`)
// ---------------------------------------------------------------------------

export function emptyDisplacement() {
  return {
    version: SURFACE_DISPLACEMENT_VERSION,
    status: "idle",            // idle | active | suspended
    snapshot: null,            // frozen source/pump/target at start
    baseline: null,            // { reading, at }
    readings: [],              // absolute pump-counter entries
    surfaceAck: null,          // { at, boundaryStrokes, boundaryCounterReading, cumulativeAtAck, carryOverStrokes }
    zeroConfirm: null,         // { at, sourceSignature, sourceValue, sourceUnit }
    pendingAnomaly: null,      // { reading, at, prevReading }
    sourceChangeAck: null,     // { at, signature }
    audit: [],                 // { at, action, detail }
    operations: [],            // archived earlier operations on this sheet
  };
}

/** Normalise a possibly-absent / legacy displacement block. Never mutates. */
export function readDisplacement(rec) {
  const d = rec && rec.displacement;
  if (!d || typeof d !== "object") return emptyDisplacement();
  return { ...emptyDisplacement(), ...d };
}

const _audit = (d, action, detail) => ({
  ...d, audit: [...(d.audit || []), { at: _now(), action, detail: detail || "" }],
});

/** Operator confirms that a complete, valid Card 4 volume really is zero. */
export function confirmZeroVolume(disp, source) {
  if (!source || source.state !== "zero") return disp;
  const next = {
    ...disp,
    zeroConfirm: {
      at: _now(),
      sourceSignature: sourceSignature({
        rigId: source.rigId, sourceValue: source.sourceValue, sourceUnit: source.sourceUnit,
        pumpOutputBblStk: source.pumpOutputBblStk,
      }),
      sourceValue: source.sourceValue,
      sourceUnit: source.sourceUnit,
      rigId: source.rigId,
    },
  };
  return _audit(next, "zero-volume-confirmed",
    `Card 4 Surface Line Volume confirmed zero (${source.sourceValue} ${source.sourceUnit}).`);
}

export function clearZeroVolume(disp, reason) {
  if (!disp.zeroConfirm) return disp;
  return _audit({ ...disp, zeroConfirm: null }, "zero-volume-confirmation-invalidated", reason || "");
}

/**
 * Start a tracked displacement. Requires an explicitly entered baseline
 * pump-counter reading — never assumed zero, never taken from an earlier
 * entry.
 */
export function startOperation(disp, { snapshot, baselineReading }) {
  const reading = _num(baselineReading);
  if (reading == null || reading < 0) {
    return { error: "Enter the current pump-counter reading to start this displacement." };
  }
  if (!snapshot) return { error: "Source data is incomplete — cannot start tracking." };

  // Starting again archives whatever came before, with its full history.
  const archived = disp.snapshot
    ? [...(disp.operations || []), {
        snapshot: disp.snapshot, baseline: disp.baseline, readings: disp.readings,
        surfaceAck: disp.surfaceAck, closedAt: _now(), closedReason: "restarted",
      }]
    : (disp.operations || []);

  const next = {
    ...emptyDisplacement(),
    operations: archived,
    audit: disp.audit || [],
    zeroConfirm: disp.zeroConfirm || null,
    status: "active",
    snapshot: { ...snapshot, startedAt: _now() },
    baseline: { reading, at: _now() },
    readings: [],
  };
  return {
    disp: _audit(next, "operation-started",
      `Baseline pump-counter reading ${reading}. Target ${snapshot.targetStrokes == null ? "—" : snapshot.targetStrokes.toFixed(4)} strokes.`),
  };
}

const _effective = (disp) => (disp.readings || []).filter((r) => !r.voided);

const _lastReadingValue = (disp) => {
  const eff = _effective(disp);
  if (eff.length > 0) return _num(eff[eff.length - 1].reading);
  return disp.baseline ? _num(disp.baseline.reading) : null;
};

/**
 * Record a manual absolute pump-counter entry.
 *
 * A reading BELOW the previous effective reading is never turned into
 * negative strokes and never assumed to be a counter reset: it is parked as
 * `pendingAnomaly` and requires an explicit operator resolution.
 */
export function addReading(disp, { reading, note }) {
  if (disp.status !== "active") return { error: "Start the displacement before entering readings." };
  if (disp.pendingAnomaly) return { error: "Resolve the flagged reading before entering another." };
  const n = _num(reading);
  if (n == null || n < 0) return { error: "Enter a valid pump-counter reading." };
  const prev = _lastReadingValue(disp);
  if (prev != null && n < prev) {
    return {
      disp: _audit({ ...disp, pendingAnomaly: { reading: n, at: _now(), prevReading: prev } },
        "reading-flagged",
        `Entered reading ${n} is below the previous reading ${prev} — awaiting operator resolution.`),
      flagged: true,
    };
  }
  const entry = { id: _id(), reading: n, at: _now(), kind: "reading", voided: false, note: note || "" };
  return {
    disp: _audit({ ...disp, readings: [...(disp.readings || []), entry] },
      "reading-added", `Pump-counter reading ${n}.`),
  };
}

/**
 * Resolve a flagged (lower-than-previous) entry.
 *   "correction"    — the PREVIOUS entry was mistyped: void it and record
 *                     the flagged value as a correction in its place.
 *   "counter-reset" — counter reset / rollover. Accounting cannot continue
 *                     across an unknown discontinuity: the operation is
 *                     suspended with its history intact and the operator
 *                     must start a new tracked displacement with a fresh
 *                     baseline.
 *   "pump-change"   — same treatment as a reset (mixing unequal pump
 *                     strokes in one count is out of scope).
 *   "discard"       — the entry was a mistake; nothing is recorded.
 */
export function resolveAnomaly(disp, { resolution, note }) {
  const a = disp.pendingAnomaly;
  if (!a) return { error: "No flagged reading to resolve." };

  if (resolution === "discard") {
    return { disp: _audit({ ...disp, pendingAnomaly: null }, "reading-discarded",
      `Flagged reading ${a.reading} discarded by operator. ${note || ""}`.trim()) };
  }

  if (resolution === "correction") {
    const eff = _effective(disp);
    if (eff.length === 0) {
      // Correcting the baseline itself.
      const base = _num(disp.baseline && disp.baseline.reading);
      if (base == null) return { error: "No baseline to correct." };
      return {
        disp: _audit({
          ...disp, pendingAnomaly: null,
          baseline: { reading: a.reading, at: _now(), correctedFrom: base },
        }, "baseline-corrected", `Baseline reading corrected ${base} → ${a.reading}. ${note || ""}`.trim()),
      };
    }
    const last = eff[eff.length - 1];
    const priorValue = eff.length > 1 ? _num(eff[eff.length - 2].reading)
      : _num(disp.baseline && disp.baseline.reading);
    if (priorValue != null && a.reading < priorValue) {
      return { error: `A corrected reading of ${a.reading} would still be below the preceding reading ${priorValue}. Correct the earlier entry first.` };
    }
    const readings = (disp.readings || []).map((r) =>
      r.id === last.id ? { ...r, voided: true, voidedAt: _now(), voidReason: "corrected" } : r);
    readings.push({
      id: _id(), reading: a.reading, at: _now(), kind: "correction",
      corrects: last.id, voided: false, note: note || "",
    });
    return {
      disp: _audit({ ...disp, readings, pendingAnomaly: null }, "reading-corrected",
        `Reading ${last.reading} corrected to ${a.reading}. ${note || ""}`.trim()),
    };
  }

  if (resolution === "counter-reset" || resolution === "pump-change") {
    return {
      disp: _audit({ ...disp, pendingAnomaly: null, status: "suspended",
        suspendedReason: resolution },
      resolution === "counter-reset" ? "counter-reset-recorded" : "pump-change-recorded",
      `Operator reported a ${resolution === "counter-reset" ? "counter reset / rollover" : "pump change"} at reading ${a.reading}. Tracking suspended with history retained; a new tracked displacement with a fresh baseline is required. ${note || ""}`.trim()),
    };
  }

  return { error: "Unknown resolution." };
}

/** Correct the most recent effective entry to a new (higher) value. */
export function correctLastReading(disp, { reading, note }) {
  const eff = _effective(disp);
  if (eff.length === 0) return { error: "No entries to correct." };
  const n = _num(reading);
  if (n == null || n < 0) return { error: "Enter a valid pump-counter reading." };
  const last = eff[eff.length - 1];
  const priorValue = eff.length > 1 ? _num(eff[eff.length - 2].reading)
    : _num(disp.baseline && disp.baseline.reading);
  if (priorValue != null && n < priorValue) {
    return { error: `A corrected reading of ${n} would be below the preceding reading ${priorValue}.` };
  }
  const readings = (disp.readings || []).map((r) =>
    r.id === last.id ? { ...r, voided: true, voidedAt: _now(), voidReason: "corrected" } : r);
  readings.push({ id: _id(), reading: n, at: _now(), kind: "correction",
    corrects: last.id, voided: false, note: note || "" });
  return {
    disp: _audit({ ...disp, readings }, "reading-corrected",
      `Reading ${last.reading} corrected to ${n}. ${note || ""}`.trim()),
  };
}

/**
 * Operator acknowledges the calculated surface-stage boundary.
 *
 * The boundary is stamped at the CALCULATED stroke count, not at the moment
 * of the click: strokes already recorded beyond it carry into the next
 * stage instead of being discarded or re-counted. Acknowledging twice is a
 * no-op.
 */
export function acknowledgeSurfaceBoundary(disp) {
  if (disp.surfaceAck) return { disp };          // idempotent
  const view = deriveDisplacement({ displacement: disp });
  if (!view.surface || !view.surface.reached) {
    return { error: "The calculated surface-stage target has not been reached yet." };
  }
  const ack = {
    at: _now(),
    boundaryStrokes: view.surface.targetStrokes,
    boundaryCounterReading: view.surface.boundaryCounterReading,
    cumulativeAtAck: view.cumulativeStrokes,
    carryOverStrokes: view.nextStage.stageStrokes,
  };
  return {
    disp: _audit({ ...disp, surfaceAck: ack }, "surface-boundary-acknowledged",
      `Surface lines displaced — working stroke counter reset for drill-string tracking. Boundary stamped at ${ack.boundaryStrokes.toFixed(4)} strokes (pump-counter ${ack.boundaryCounterReading}). Reset confirmed at cumulative ${ack.cumulativeAtAck.toFixed(4)}; ${ack.carryOverStrokes.toFixed(4)} strokes beyond the boundary retained in the record.`),
  };
}

/** Record that the operator reviewed a Card 4 / pump-output source change. */
export function acknowledgeSourceChange(disp, signature, note) {
  return _audit({ ...disp, sourceChangeAck: { at: _now(), signature } },
    "source-change-reviewed", note || `Source signature now ${signature}.`);
}

// ---------------------------------------------------------------------------
// 4. Derivation — the only place stage/cumulative numbers are produced
// ---------------------------------------------------------------------------

/**
 * deriveDisplacement(rec) → view model. Pure.
 *
 * Keeps four things strictly apart:
 *   A. entries          — the recorded absolute pump-counter readings.
 *   B. cumulativeStrokes— strokes since this operation's baseline.
 *   C. surface.stageStrokes / nextStage.stageStrokes — progress inside one
 *      stage, measured from that stage's own boundary.
 *   D. history          — archived operations and the acknowledged boundary.
 */
export function deriveDisplacement(rec) {
  const disp = readDisplacement(rec);
  const snap = disp.snapshot;
  const entries = (disp.readings || []).map((r) => ({ ...r }));
  const eff = entries.filter((r) => !r.voided);

  const baselineReading = disp.baseline ? _num(disp.baseline.reading) : null;
  const latestReading = _lastReadingValue(disp);
  const cumulativeStrokes = (baselineReading != null && latestReading != null)
    ? Math.max(0, latestReading - baselineReading) : 0;

  const target = snap ? _num(snap.targetStrokes) : null;

  // Per-entry deltas + stage allocation. The allocation is what proves no
  // stroke is lost or counted twice when an entry crosses the boundary.
  let running = baselineReading;
  let runningCum = 0;
  const log = eff.map((r) => {
    const v = _num(r.reading);
    const delta = (running != null && v != null) ? v - running : null;
    running = v;
    const cum = (baselineReading != null && v != null) ? Math.max(0, v - baselineReading) : null;
    let toSurface = null; let toDrillString = null;
    if (delta != null && target != null) {
      const prevCum = runningCum;
      toSurface = Math.min(Math.max(target - prevCum, 0), delta);
      toDrillString = delta - toSurface;
    }
    if (cum != null) runningCum = cum;
    return {
      ...r,
      strokesSincePrevious: delta,
      cumulativeStrokes: cum,
      allocatedToSurface: toSurface,
      allocatedToDrillString: toDrillString,
      crossedBoundary: toSurface != null && toSurface > 0 && toDrillString > 0,
    };
  });

  const zeroConfirmed = Boolean(disp.zeroConfirm);

  let surface = null;
  if (snap && target != null) {
    const notRequired = target === 0;
    const reached = notRequired || cumulativeStrokes + EPS >= target;
    const stageStrokes = Math.min(cumulativeStrokes, target);
    surface = {
      key: "surface",
      label: "Surface Line Displacement",
      targetStrokes: target,
      wholeStrokeTarget: snap.wholeStrokeTarget,
      stageStrokes,
      remainingStrokes: Math.max(0, target - cumulativeStrokes),
      reached,
      notRequired,
      boundaryCounterReading: baselineReading != null ? baselineReading + target : null,
      acknowledged: Boolean(disp.surfaceAck),
      status: notRequired ? "not-required"
        : disp.surfaceAck ? "complete"
        : reached ? "awaiting-acknowledgement"
        : "in-progress",
      statusLabel: notRequired
        ? "Not required: confirmed zero volume"
        : disp.surfaceAck ? "Calculated target reached · acknowledged"
        : reached ? "Calculated target reached — confirm to continue"
        : "In progress",
    };
  }

  // The drill-string stage starts at the surface boundary. Its counter is
  // zero AT that boundary — strokes recorded past it are real progress and
  // are shown as such, whether or not the operator has acknowledged yet.
  const pastBoundary = (target != null) ? Math.max(0, cumulativeStrokes - target) : 0;
  const ack = disp.surfaceAck || null;
  const nextStage = {
    key: "drillstring",
    label: "Drill-String Displacement",
    stageStrokes: pastBoundary,
    // Visible as the MAIN stage only after acknowledgement (or when the
    // surface stage is not required at all).
    visible: Boolean(surface && (surface.acknowledged || surface.notRequired)),
    startedAtCounterReading: surface ? surface.boundaryCounterReading : null,
    carryOverAtAck: ack ? ack.carryOverStrokes : null,
    // The working display after a manual reset counts from the RESET, which
    // is what the operator sees on the counter. The calculated boundary is
    // kept separately above so a late reset never moves it.
    resetCounterReading: (ack && baselineReading != null)
      ? baselineReading + ack.cumulativeAtAck : null,
    strokesSinceReset: ack ? Math.max(0, cumulativeStrokes - ack.cumulativeAtAck) : null,
    resetLateByStrokes: ack ? Math.max(0, ack.cumulativeAtAck - ack.boundaryStrokes) : null,
  };

  return {
    status: disp.status,
    suspendedReason: disp.suspendedReason || "",
    snapshot: snap,
    baseline: disp.baseline || null,
    baselineReading,
    latestReading,
    cumulativeStrokes,
    entries: log,
    voidedEntries: entries.filter((r) => r.voided),
    surface,
    nextStage,
    ack: disp.surfaceAck || null,
    pendingAnomaly: disp.pendingAnomaly || null,
    zeroConfirmed,
    zeroConfirm: disp.zeroConfirm || null,
    sourceChangeAck: disp.sourceChangeAck || null,
    audit: disp.audit || [],
    archivedOperations: disp.operations || [],
    activeStageKey: nextStage.visible ? "drillstring" : "surface",
  };
}
